// リスクアセスメント（#2234）のドメイン型。
// 設問文言・ラベル・閾値はすべてサーバー（GET /api/v1/risk_assessments/new）から配信されるため、
// フロントでは構造のみを定義し、文言や閾値を二重定義しない。

// risk_assessment_answers.answer_type と一致させる（1: 該当 / 2: 非該当 / 3: 不明）。
// 0（未回答）は「回答しないまま完了した」ことを表すサーバー側の保存値。
// 画面上の未選択状態は AnswerDraft の answerType: null で表し、送信時に 0 へ変換する。
export const ANSWER_TYPES = {
  unanswered: 0,
  applicable: 1,
  notApplicable: 2,
  unknown: 3
} as const;

export type RiskAssessmentAnswerType = (typeof ANSWER_TYPES)[keyof typeof ANSWER_TYPES];

// 「どちらにも該当しない」（スキップ）を表す画面上の擬似 versionType。
// selectedVersionType の null は「未選択」の意味で既に使っているため、別の値で区別する。
// サーバーへは version_type: null として送る（risk_assessments.version_type IS NULL がスキップ）。
export const SKIPPED_VERSION_TYPE = 0;

export type RiskAssessmentConsultableType =
  | "AnytimeConsultation"
  | "Reservation"
  | "DaytimeAdviceSheet";

export type RiskAssessmentDeniedReason =
  | "not_assigned"
  | "operation_period_expired";

// risk_assessments.status（RiskAssessment::STATUSES）のキー名。
export type RiskAssessmentStatus = "draft" | "completed";

export type RiskAssessmentQuestionDetail = {
  key: string;
  label: string;
  // 入れ子サブ項目（妊娠Ver Q17 のみ。他は空配列）。親はラベル表示のみで detail_key として保存しない。
  children: RiskAssessmentQuestionDetail[];
};

export type RiskAssessmentQuestion = {
  questionNo: number;
  categoryKey: string;
  text: string;
  applicableLabel: string;
  multiSelect: boolean;
  details: RiskAssessmentQuestionDetail[];
};

export type RiskAssessmentCategory = {
  key: string;
  label: string;
  questionNos: number[];
};

export type RiskAssessmentVersion = {
  versionType: number;
  key: string;
  label: string;
  shortLabel: string;
  positiveThreshold: number;
  questionCount: number;
  categories: RiskAssessmentCategory[];
  questions: RiskAssessmentQuestion[];
};

export type RiskAssessmentAnswer = {
  questionNo: number;
  answerType: RiskAssessmentAnswerType;
  detailKeys: string[];
};

export type PreviousAssessment = {
  id: string;
  completedAt: string | null;
  versionType: number;
  applicableCount: number;
  positive: boolean;
  answers: RiskAssessmentAnswer[];
};

export type EditableAssessment = {
  id: string;
  // スキップしたアセスメントは null。
  versionType: number | null;
  lockVersion: number;
  completedAt: string | null;
  editableUntil: string | null;
  applicableCount: number;
  positive: boolean;
  answers: RiskAssessmentAnswer[];
};

export type RiskAssessmentConsultable = {
  type: RiskAssessmentConsultableType;
  id: string;
  serviceLabel: string;
};

export type RiskAssessmentVersionBasis = {
  source: string | null;
  baseDate: string | null;
};

// ボトムバーの活性判定だけに使う軽量レスポンス（GET /api/v1/risk_assessments/status）。
// 過去の回答内容と設問カタログは含まれない。入力画面を開くときは RiskAssessmentNewResponse を使う。
export type RiskAssessmentEditableStatus = {
  id: string;
  versionType: number | null;
  completedAt: string | null;
  editableUntil: string | null;
  editable: boolean;
};

export type RiskAssessmentStatusResponse = {
  startable: boolean;
  deniedReason: RiskAssessmentDeniedReason | null;
  unlimited: boolean;
  operableUntil: string | null;
  consultable: RiskAssessmentConsultable;
  defaultVersionType: number | null;
  editableAssessment: RiskAssessmentEditableStatus | null;
};

export type RiskAssessmentNewResponse = {
  startable: boolean;
  deniedReason: RiskAssessmentDeniedReason | null;
  unlimited: boolean;
  operableUntil: string | null;
  consultable: RiskAssessmentConsultable;
  defaultVersionType: number | null;
  versionBasis: RiskAssessmentVersionBasis;
  versions: RiskAssessmentVersion[];
  // 「どちらにも該当しない」選択肢のラベル。文言はサーバーから配信しフロントに二重定義しない。
  skipVersionLabel: string;
  // キーは versionType の文字列。該当Verの前回入力が無い場合は null（REQ-006-2）。
  previousAssessments: Record<string, PreviousAssessment | null>;
  editableAssessment: EditableAssessment | null;
};

export type RiskAssessmentCompletionMessage = {
  title: string;
  body: string[];
  reportActionLabel: string;
};

export type RiskAssessmentResult = {
  id: string;
  versionType: number;
  versionLabel: string;
  lockVersion: number;
  status: RiskAssessmentStatus;
  completedAt: string | null;
  editableUntil: string | null;
  applicableCount: number;
  notApplicableCount: number;
  unknownCount: number;
  positive: boolean;
  positiveThreshold: number;
  alertFired: boolean;
  completionMessage: RiskAssessmentCompletionMessage;
};

// リクエストボディのみ snake_case（サーバーの Strong Parameters に合わせる）。
export type RiskAssessmentAnswerInput = {
  question_no: number;
  answer_type: RiskAssessmentAnswerType;
  detail_keys: string[];
};

export type RiskAssessmentCreatePayload = {
  consultable_type: RiskAssessmentConsultableType;
  consultable_id: string;
  // スキップ時は null。このとき answers は空配列でなければサーバーが 422 で弾く。
  version_type: number | null;
  answers: RiskAssessmentAnswerInput[];
};

export type RiskAssessmentUpdatePayload = RiskAssessmentCreatePayload & {
  lock_version: number;
};

// 相談サイドバーの「リスクアセスメント履歴」からの読み取り専用閲覧（GET /api/v1/risk_assessments/:id）。
// 設問カタログ（全バージョン分）と完了済みアセスメントの回答内容を返す。
export type RiskAssessmentViewResponse = {
  versions: RiskAssessmentVersion[];
  assessment: PreviousAssessment;
};
